import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { TopBar } from '../components/TopBar'
import { Avatar, roleLabels } from '../components/Avatar'
import { EventCard } from '../components/EventCard'
import { CoverResponseInline } from '../components/CoverResponseInline'
import { ActionableRoutinesWidget, ActionableTasksWidget } from '../components/ActionableWidgets'
import { InlineHandoverWidget } from '../components/InlineHandoverWidget'
import { MedicationReminderWatcher } from '../components/MedicationReminderWatcher'
import { useApp } from '../store/AppContext'
import { localISODate } from '../lib/date'
import { eventsNeedingCover } from '../lib/cover'
import { formatTime12Hour } from '../lib/time'
import { formatMedicationTime, loadMedications, saveMedications, todayKey, type MedicationReminder } from '../lib/medications'
import type { ScheduleEvent } from '../types'
import {
  allowedTodaySections,
  defaultTodaySections,
  loadTodayPreferences,
  saveTodayPreferences,
  type TodaySectionId,
} from '../lib/todayLayout'

const sectionMeta: Record<TodaySectionId, { label: string; icon: string }> = {
  support_today: { label: "Who's supporting today", icon: '👤' },
  schedule: { label: "Today's calendar", icon: '📅' },
  tasks: { label: 'Tasks', icon: '✅' },
  routines: { label: 'Routines', icon: '🔁' },
  handover: { label: 'Shift handover', icon: '🔄' },
  cover: { label: 'Shift cover', icon: '⚠️' },
  updates: { label: 'Team updates', icon: '📣' },
}

function SupportWorkerMedicationCard() {
  const [items, setItems] = useState<MedicationReminder[]>(loadMedications)
  const today = todayKey()

  useEffect(() => {
    const reload = () => setItems(loadMedications())
    window.addEventListener('schub:medications-change', reload)
    return () => window.removeEventListener('schub:medications-change', reload)
  }, [])

  const toggleTaken = (item: MedicationReminder) => {
    const next = items.map(current => {
      if (current.id !== item.id) return current
      if (current.takenDate === today) return { ...current, takenDate: undefined, takenAt: undefined }
      const now = new Date()
      return {
        ...current,
        takenDate: today,
        takenAt: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
      }
    })
    setItems(next)
    saveMedications(next)
  }

  const sorted = [...items].sort((a, b) => a.time.localeCompare(b.time))

  return (
    <section className="overflow-hidden rounded-[22px] border border-[#dbe6ef] bg-white shadow-[0_6px_20px_rgba(15,23,42,.055)]">
      <div className="flex items-center justify-between gap-3 bg-[#f2f0fb] px-4 py-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[.12em] text-[#7558b5]">Alex’s medication</p>
          <h3 className="mt-0.5 text-sm font-black text-slate-900">Medication reminders</h3>
        </div>
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-base text-[#7558b5] shadow-sm">🔔</div>
      </div>
      <div className="divide-y divide-slate-100 px-4">
        {sorted.map(item => {
          const taken = item.takenDate === today
          return (
            <div key={item.id} className="flex items-center gap-3 py-3">
              <button
                onClick={() => toggleTaken(item)}
                aria-label={taken ? `Mark ${item.name} not taken` : `Mark ${item.name} taken`}
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border-2 text-sm font-black ${taken ? 'border-[#218b89] bg-[#218b89] text-white' : 'border-slate-300 bg-white text-transparent'}`}
              >
                ✓
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-xs font-black text-slate-900">{item.name}</p>
                  {item.reminderEnabled && <span className="shrink-0 text-[11px]">🔔</span>}
                </div>
                <p className="mt-0.5 text-[10px] font-semibold text-slate-500">
                  {item.dose || 'As prescribed'}{item.instructions ? ` · ${item.instructions}` : ''}
                </p>
                {taken && (
                  <p className="mt-1 text-[10px] font-black text-[#177f80]">
                    Taken ✓ {item.takenAt ? `at ${formatMedicationTime(item.takenAt)}` : ''}
                  </p>
                )}
              </div>
              <div className="shrink-0 text-right">
                <p className="text-[9px] font-black uppercase tracking-wide text-slate-400">Due</p>
                <p className="mt-0.5 text-xs font-black text-[#245a9d]">{formatMedicationTime(item.time)}</p>
              </div>
            </div>
          )
        })}
      </div>
      <div className="border-t border-slate-100 bg-[#fbfcfe] px-4 py-2.5">
        <p className="text-[10px] font-semibold leading-4 text-slate-500">Reminder bells use Alex’s medication schedule. Mark Taken when medication has been confirmed as taken.</p>
      </div>
    </section>
  )
}

export function Today() {
  const { currentUser, events, visibleUpdates, allUsers, interfaceMode, can, pendingCoverRequestsFor } = useApp()
  const todayISO = localISODate()
  const [prefs, setPrefs] = useState(() => loadTodayPreferences(currentUser.id, currentUser.role))
  const [customising, setCustomising] = useState(false)
  const isParticipant = currentUser.role === 'participant'
  const isAdultSupportWorker = currentUser.role === 'support_worker' && currentUser.id !== 'u-casey'

  const todaysEvents = events
    .filter(event => event.date === todayISO)
    .filter(event => currentUser.role === 'support_worker' ? event.assignees.some(a => a.userId === currentUser.id) : true)
    .sort((a, b) => a.startTime.localeCompare(b.startTime))

  const demoSupportWorker = allUsers.find(user => user.id === 'u-jess') ?? allUsers.find(user => user.role === 'support_worker')
  const demoSupportEvent: ScheduleEvent | undefined = demoSupportWorker ? {
    id: 'demo-today-support',
    title: 'Support Shift',
    type: 'shift',
    date: todayISO,
    startTime: '09:00',
    endTime: '13:00',
    assignees: [{ userId: demoSupportWorker.id, status: 'accepted' }],
    createdByUserId: currentUser.id,
    notes: 'Morning routine, community access and lunch.',
  } : undefined

  const demoMovieEvent: ScheduleEvent = {
    id: 'demo-today-spiderman',
    title: 'Movies — See Spiderman',
    type: 'general',
    date: todayISO,
    startTime: '11:00',
    endTime: '13:30',
    assignees: [{ userId: currentUser.id, status: 'accepted' }],
    createdByUserId: currentUser.id,
    notes: 'Going to the movies to see Spiderman.',
  }

  const actualSupportEvent = todaysEvents.find(event => event.type === 'shift')
  const supportEvent = actualSupportEvent ?? (isParticipant ? demoSupportEvent : undefined)
  const supportUser = supportEvent?.assignees
    .map(a => allUsers.find(user => user.id === a.userId))
    .find(user => user?.role === 'support_worker')

  const participantCalendarEvents = [...todaysEvents]
  if (isParticipant && demoSupportEvent && !participantCalendarEvents.some(event => event.type === 'shift')) participantCalendarEvents.push(demoSupportEvent)
  if (isParticipant && !participantCalendarEvents.some(event => event.title.toLowerCase().includes('spiderman'))) participantCalendarEvents.push(demoMovieEvent)
  const todayCalendarEvents = participantCalendarEvents.sort((a, b) => a.startTime.localeCompare(b.startTime))

  const shiftsNeedingCover = can('manage_cover') ? eventsNeedingCover(events).filter(event => event.date >= todayISO) : []
  const myCoverRequests = can('cover_shifts') ? pendingCoverRequestsFor(currentUser.id) : []
  const pendingForMe = events.filter(event => event.assignees.some(a => a.userId === currentUser.id && a.status === 'pending'))
  const recentUpdates = visibleUpdates.slice(0, 3)
  const large = isParticipant && (interfaceMode === 'simple' || interfaceMode === 'visual')
  const greeting = new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 18 ? 'Good afternoon' : 'Good evening'

  const saveSections = (sectionIds: TodaySectionId[]) => {
    const next = { userId: currentUser.id, sectionIds }
    setPrefs(next)
    saveTodayPreferences(next)
  }

  const toggleSection = (id: TodaySectionId) => {
    saveSections(prefs.sectionIds.includes(id) ? prefs.sectionIds.filter(item => item !== id) : [...prefs.sectionIds, id])
  }

  const show = (id: TodaySectionId) => {
    if (isParticipant && (id === 'support_today' || id === 'schedule')) return true
    return prefs.sectionIds.includes(id)
  }

  return (
    <div className={isAdultSupportWorker ? 'min-h-screen bg-[#f5f8fb] pb-28 text-slate-900' : ''}>
      {isAdultSupportWorker && <MedicationReminderWatcher enabled />}
      <TopBar title="Today" subtitle={isAdultSupportWorker ? 'Your work with Alex today' : 'What matters for the day'} />

      <div className={`flex flex-col gap-4 ${isAdultSupportWorker ? 'px-3.5 py-4' : 'px-4 py-4'}`}>
        <div className={isAdultSupportWorker ? 'overflow-hidden rounded-[24px] border border-[#dbe6ef] bg-gradient-to-r from-[#173f82] via-[#245da8] to-[#3374bc] p-4 text-white shadow-[0_8px_24px_rgba(36,79,132,.10)]' : `rounded-3xl bg-white shadow-card ${large ? 'p-5' : 'p-4'}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className={`text-xs font-bold ${isAdultSupportWorker ? 'text-blue-100' : 'text-brand-600'}`}>{greeting}</p>
              <h2 className={`${large ? 'text-2xl' : 'text-xl'} mt-1 font-extrabold ${isAdultSupportWorker ? 'text-white' : 'text-slate-900'}`}>{currentUser.name.split(' ')[0]} 👋</h2>
              <p className={`mt-1 text-sm ${isAdultSupportWorker ? 'font-semibold text-blue-100' : 'text-slate-500'}`}>{isAdultSupportWorker ? 'You’re supporting Alex Turner.' : 'Here is your day in one place.'}</p>
            </div>
            {!isAdultSupportWorker && (
              <button onClick={() => setCustomising(true)} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600">Customise</button>
            )}
          </div>
          {isAdultSupportWorker && (
            <div className="mt-4 rounded-2xl bg-white/10 px-3 py-2.5">
              <p className="text-[10px] font-black uppercase tracking-[.12em] text-blue-100">Adult support worker</p>
              <p className="mt-1 text-[11px] font-semibold leading-5 text-white/90">Shift information, medication reminders, tasks, routines and handover stay together here.</p>
            </div>
          )}
        </div>

        {isAdultSupportWorker && <SupportWorkerMedicationCard />}

        {show('support_today') && (
          <section className={`rounded-2xl border bg-white ${isAdultSupportWorker ? 'border-[#dbe6ef] p-4 shadow-[0_5px_18px_rgba(15,23,42,.05)]' : `border-slate-200 shadow-card ${large ? 'p-5' : 'p-4'}`}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-extrabold text-slate-900">👤 Supporting today</h3>
              <Link to="/team" className={`text-xs font-bold ${isAdultSupportWorker ? 'text-[#245a9d]' : 'text-brand-600'}`}>My Team</Link>
            </div>
            {supportUser ? (
              <div className="mt-3 flex items-center gap-3">
                <Avatar user={supportUser} size={large ? 52 : 42} />
                <div>
                  <p className="font-bold text-slate-900">{supportUser.name}</p>
                  <p className="text-xs text-slate-500">{formatTime12Hour(supportEvent?.startTime ?? '09:00')}–{formatTime12Hour(supportEvent?.endTime ?? '13:00')} · {supportEvent?.title ?? 'Support Shift'}</p>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-500">{isAdultSupportWorker ? 'No shift assigned to you today.' : 'No support worker scheduled today.'}</p>
            )}
          </section>
        )}

        {show('cover') && (myCoverRequests.length > 0 || shiftsNeedingCover.length > 0 || pendingForMe.length > 0) && (
          <section className="flex flex-col gap-3">
            {myCoverRequests.map(request => <CoverResponseInline key={request.id} requestId={request.id} variant="card" />)}
            {shiftsNeedingCover.map(event => (
              <div key={event.id} className="rounded-2xl border-2 border-rose-200 bg-rose-50 p-4">
                <p className="text-xs font-bold uppercase text-rose-600">Needs Cover</p>
                <p className="mt-1 font-extrabold text-rose-900">{event.title}</p>
                <p className="text-sm text-rose-700">{event.date} · {formatTime12Hour(event.startTime)}–{formatTime12Hour(event.endTime)}</p>
                <Link to="/calendar" className="mt-3 inline-block text-xs font-bold text-rose-700">Find cover →</Link>
              </div>
            ))}
            {pendingForMe.map(event => <EventCard key={event.id} event={event} showDate />)}
          </section>
        )}

        {show('schedule') && (
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className={`${large ? 'text-lg' : 'text-sm'} font-extrabold text-slate-900`}>📅 Today’s calendar</h3>
              <Link to="/calendar" className={`text-xs font-bold ${isAdultSupportWorker ? 'text-[#245a9d]' : 'text-brand-600'}`}>Full calendar</Link>
            </div>
            <div className="flex flex-col gap-2">
              {todayCalendarEvents.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-5 text-center text-sm text-slate-400">Nothing scheduled for today.</div>}
              {todayCalendarEvents.map(event => <EventCard key={event.id} event={event} />)}
            </div>
          </section>
        )}

        {show('tasks') && (
          <section className={`rounded-2xl border bg-white ${isAdultSupportWorker ? 'border-slate-200 p-4 shadow-[0_5px_18px_rgba(15,23,42,.05)]' : `border-slate-200 shadow-card ${large ? 'p-5' : 'p-4'}`}`}>
            <div className="flex items-center justify-between">
              <h3 className={`${large ? 'text-lg' : 'text-sm'} font-extrabold text-slate-900`}>✅ Tasks</h3>
              <Link to="/tasks" className={`text-xs font-bold ${isAdultSupportWorker ? 'text-[#245a9d]' : 'text-brand-600'}`}>See all</Link>
            </div>
            <ActionableTasksWidget compact={!large} />
          </section>
        )}

        {show('routines') && (
          <section className={`rounded-2xl border bg-white ${isAdultSupportWorker ? 'border-slate-200 p-4 shadow-[0_5px_18px_rgba(15,23,42,.05)]' : `border-slate-200 shadow-card ${large ? 'p-5' : 'p-4'}`}`}>
            <div className="flex items-center justify-between">
              <h3 className={`${large ? 'text-lg' : 'text-sm'} font-extrabold text-slate-900`}>🔁 Routines</h3>
              <Link to="/routines" className={`text-xs font-bold ${isAdultSupportWorker ? 'text-[#245a9d]' : 'text-brand-600'}`}>See all</Link>
            </div>
            <ActionableRoutinesWidget compact={!large} />
          </section>
        )}

        {currentUser.role === 'support_worker' && show('handover') && <InlineHandoverWidget />}

        {show('updates') && recentUpdates.length > 0 && (
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-extrabold text-slate-900">📣 Team Updates</h3>
              <Link to="/updates" className={`text-xs font-bold ${isAdultSupportWorker ? 'text-[#245a9d]' : 'text-brand-600'}`}>See all</Link>
            </div>
            <div className="flex flex-col gap-2">
              {recentUpdates.map(update => {
                const author = allUsers.find(user => user.id === update.authorId)
                if (!author) return null
                return (
                  <Link key={update.id} to="/updates" className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-card">
                    <Avatar user={author} size={34} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-slate-900">{author.name} · <span className="font-normal text-slate-400">{roleLabels[author.role]}</span></p>
                      <p className="mt-1 line-clamp-2 text-sm text-slate-600">{update.text}</p>
                    </div>
                  </Link>
                )
              })}
            </div>
          </section>
        )}

        {isAdultSupportWorker && (
          <section>
            <p className="px-1 text-[10px] font-black uppercase tracking-[.12em] text-[#245a9d]">Work tools</p>
            <div className="mt-2 grid grid-cols-3 gap-2.5">
              <Link to="/chats" className="rounded-[18px] border border-slate-200 bg-white p-3 text-center shadow-[0_4px_14px_rgba(15,23,42,.04)]"><div className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl bg-[#f0ecfb]">💬</div><p className="mt-2 text-[10px] font-black">Messages</p></Link>
              <Link to="/handover" className="rounded-[18px] border border-slate-200 bg-white p-3 text-center shadow-[0_4px_14px_rgba(15,23,42,.04)]"><div className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl bg-[#e9f7f4]">✓</div><p className="mt-2 text-[10px] font-black">Handover</p></Link>
              <Link to="/more" className="rounded-[18px] border border-slate-200 bg-white p-3 text-center shadow-[0_4px_14px_rgba(15,23,42,.04)]"><div className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl bg-[#edf4ff]">•••</div><p className="mt-2 text-[10px] font-black">More</p></Link>
            </div>
          </section>
        )}
      </div>

      {customising && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/40 p-4">
          <div className="mx-auto mt-10 max-w-md rounded-3xl bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between">
              <div><h2 className="text-lg font-extrabold text-slate-900">Customise Today</h2><p className="mt-1 text-xs text-slate-500">Choose the day-to-day sections you want to see.</p></div>
              <button onClick={() => setCustomising(false)} className="text-xl text-slate-400">×</button>
            </div>
            <div className="mt-4 flex flex-col gap-2">
              {allowedTodaySections(currentUser.role).map(id => (
                <label key={id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                  <input type="checkbox" checked={show(id)} disabled={isParticipant && (id === 'support_today' || id === 'schedule')} onChange={() => toggleSection(id)} />
                  <span className="text-xl">{sectionMeta[id].icon}</span>
                  <span className="text-sm font-semibold text-slate-800">{sectionMeta[id].label}</span>
                </label>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => saveSections(defaultTodaySections(currentUser.role))} className="flex-1 rounded-xl border border-slate-200 py-3 text-xs font-bold text-slate-600">Reset defaults</button>
              <button onClick={() => setCustomising(false)} className="flex-1 rounded-xl bg-brand-600 py-3 text-xs font-bold text-white">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

